// ** React Imports
import { useHistory } from "react-router-dom";
import { useContext } from "react";

// ** Redux & Store & Actions
import { useSelector, useDispatch } from "react-redux";
import { getUserProfileMain } from "store/modules/user-profile/actions/get-user-profile-main.action";
import { openAuthModal } from "store/modules/global-common.slice";

import { addFriend } from "store/modules/user-profile/actions/add-friend.action";
import { acceptFriend } from "store/modules/user-profile/actions/accept-friend.action";
import { deleteFriend } from "store/modules/user-profile/actions/delete-friend.action";
import { followUser } from "store/modules/user-profile/actions/follow-user.action";
import { unfollowUser } from "store/modules/user-profile/actions/unfollow-user.action";
import { blockUser } from "store/modules/user-profile/actions/block-user.action";
import { unblockUser } from "store/modules/user-profile/actions/unblock-user.action";

import { setUserFollow } from "store/modules/mainPages";

// ** Third Party Components
import { toast } from "helpers/toastWrapper";
import { useTranslation } from "react-i18next";

// ** Contexts
import authContext from "contexts/authContext";

// ** Utils & Helpers
import generatePath from "utils/generatePath";
import hasDefaultAvatarFunc from "helpers/hasDefaultAvatar";
import { VIP_STATUS } from "helpers/constants";

// ** Custom Hooks
import useGetTextLimitMessages from "hooks/useGetTextLimitMessages";

// ** SVG Static Imports
import { ReactComponent as SolidComment } from "resources/icons/solid/comment.svg";
import { ReactComponent as SolidEye } from "resources/icons/solid/eye.svg";
import { ReactComponent as SolidEyeSlash } from "resources/icons/solid/eye-slash.svg";
import { ReactComponent as SolidUserPlus } from "resources/icons/solid/user-plus.svg";
import { ReactComponent as SolidUserMinus } from "resources/icons/solid/user-minus.svg";
import { ReactComponent as SolidBan } from "resources/icons/solid/ban.svg";
import { ReactComponent as SolidExclamationCircle } from "resources/icons/solid/exclamation-circle.svg";

// #####################################################

const useActionItems = () => {
	const history = useHistory();

	const { translateMessage, redirectToSuperVip } = useGetTextLimitMessages();

	const isAuth = useContext(authContext);

	const dispatch = useDispatch();

	const { t } = useTranslation(["common", "userProfile", "suspensions"]);

	const myId = useSelector((state) => state.globalUser.id);

	const globalUserStatusVip = useSelector(
		(state) => state.globalUser.status_vip
	);
	const globalUserAvatar = useSelector((state) => state.globalUser.avatar);

	const userId = useSelector((state) => state.userProfileMain.id);
	const userTolerances = useSelector(
		(state) => state.userProfileMain.tolerances
	);
	const userFrozen = useSelector((state) => state.userProfileMain.frozen);
	const userStatus = useSelector((state) => state.userProfileMain.status);
	const userLogin = useSelector((state) => state.userProfileMain.login);
	const userSuspensionDuration = useSelector(
		(state) => state.user.suspension.duration
	);

	const isAllowed = useSelector((state) => state.userProfileMain.isAllowed);

	const isMe = userId === myId;
	const isBan = userSuspensionDuration > 0;

	const isVisible = isAllowed;

	const conversationId = `${userId}-${myId}`;

	const isNotVip = globalUserStatusVip <= VIP_STATUS.VERIFIED;
	const isLimit = userTolerances.limit;
	const isDefaultAvatar = hasDefaultAvatarFunc(globalUserAvatar);
	const canSendMessage = userTolerances.canSendMessage;

	const writeActionTooltipInfo = () => {
		if (isMe) {
			return t("userProfile:tooltip.cantSendMessageToYourself");
		} else if (isBan) {
			return t("suspensions:bannedAccount");
		} else if (isLimit) {
			if (isDefaultAvatar && isNotVip) {
				return t("common:cantCommentLimitWhenNoVipShort");
			} else if (!isDefaultAvatar && isNotVip) {
				return t("common:cantCommentLimitWhenNoVipShort");
			} else {
				return t("common:cantCommentLimitWhenVipShort");
			}
		} else {
			return t("userProfile:tooltip.cantSendMessage");
		}
	};

	// #####################################################

	return [
		!userStatus.blockByMe &&
			!userFrozen && {
				label: t(`userProfile:actionMenu.write`),
				icon: "comment",
				svgIconComponent: SolidComment,
				action: () => {
					if (!isAuth) {
						dispatch(openAuthModal());
					} else {
						if (isLimit && !canSendMessage) {
							toast.error(translateMessage);
							window.scrollTo(0, 0);
							history.push({
								pathname: `/userpanel/payments/buy`,
								state: {
									showSuperVip: redirectToSuperVip,
								},
							});

							return;
						} else {
							history.push(
								`/userpanel/messages/show/${conversationId}`
							);
						}
					}
				},
				disabled:
					isLimit && !isMe
						? false
						: isMe || (!canSendMessage && isAuth),
				tooltip: writeActionTooltipInfo(),
			},
		((isVisible && userStatus.friendStatus === 0) || isMe) &&
			!userFrozen && {
				label: t(`userProfile:actionMenu.addFriend`),
				icon: "user-plus",
				svgIconComponent: SolidUserPlus,
				action: () => {
					if (!isAuth) {
						dispatch(openAuthModal());
					} else {
						dispatch(addFriend({ userId }))
							.unwrap()
							.then((data) => {
								if (data)
									toast.success(
										t(
											"userProfile:successMessages.inviteSend"
										)
									);
							})
							.catch((error) => {
								toast.error(error.data?.error);
							});
					}
				},
				disabled:
					isMe ||
					isBan ||
					(userTolerances.canInviteFriend === false && isAuth),
				tooltip: isMe
					? t("userProfile:tooltip.cantSentInviteToYourself")
					: isBan
					? t("suspensions:bannedAccount")
					: t("userProfile:tooltip.cantSentInvite"),
			},
		isVisible &&
			!userFrozen &&
			userStatus.friendStatus === 2 && {
				label: t(`userProfile:actionMenu.addFriend`),
				icon: "user-plus",
				svgIconComponent: SolidUserPlus,
				action: () => {
					dispatch(acceptFriend({ userId }))
						.unwrap()
						.then((data) => {
							if (data)
								toast.success(
									t("userProfile:successMessages.friend")
								);
						})
						.catch((error) => {
							toast.error(error.data?.error);
						});
				},
				disabled: false,
			},
		isAllowed &&
			!userFrozen &&
			userStatus.friendStatus === 1 && {
				label: t(`userProfile:actionMenu.deleteInvite`),
				icon: "user-minus",
				svgIconComponent: SolidUserMinus,
				action: () => {
					dispatch(deleteFriend({ userId }))
						.unwrap()
						.then((data) => {
							if (data)
								toast.success(
									t(
										"userProfile:successMessages.invideDeleted"
									)
								);
						})
						.catch((error) => {
							toast.error(error.data?.error);
						});
				},
				disabled: isMe || isBan,
				tooltip: isBan ? t("suspensions:bannedAccount") : "",
			},
		isVisible &&
			!userFrozen &&
			userStatus.friendStatus === 3 && {
				label: t(`userProfile:actionMenu.deleteFriend`),
				icon: "user-minus",
				svgIconComponent: SolidUserMinus,
				action: () => {
					dispatch(deleteFriend({ userId }))
						.unwrap()
						.then((data) => {
							if (data)
								toast.success(
									t(
										"userProfile:successMessages.friendDeleted"
									)
								);
						})
						.catch((error) => {
							toast.error(error.data?.error);
						});
				},
				disabled: isMe || isBan,
				tooltip: isBan ? t("suspensions:bannedAccount") : "",
			},
		//follow Status:
		// 0 -  ani jeden ani drugi nie obserwuje
		// 1 -  ja obserwuje
		// 2 - mnie obserwuje
		// 3 - wzajemnie
		isVisible &&
			!userFrozen &&
			(userStatus.followStatus === 0 ||
				userStatus.followStatus === 2) && {
				label: t(`userProfile:actionMenu.follow`),
				icon: "eye",
				svgIconComponent: SolidEye,
				action: () => {
					if (!isAuth) {
						dispatch(openAuthModal());
					} else {
						dispatch(followUser({ userId }))
							.unwrap()
							.then((data) => {
								if (data) {
									toast.success(
										t("userProfile:successMessages.follow")
									);
									dispatch(
										setUserFollow({
											isFollowed: true,
											userId,
										})
									);
								}
							})
							.catch((error) => {
								toast.error(error.data?.error);
							});
					}
				},
				disabled:
					isMe ||
					isBan ||
					(userTolerances.canFollow === false && isAuth),
				tooltip: isMe
					? t("userProfile:tooltip.cantFollowToYourself")
					: isBan
					? t("suspensions:bannedAccount")
					: userStatus.friendStatus === 3
					? t("userProfile:tooltip.cantFollowYourFriend")
					: t("userProfile:tooltip.cantFollow"),
				inDropdown: false,
			},
		isVisible &&
			!userFrozen &&
			(userStatus.followStatus === 1 ||
				userStatus.followStatus === 3) && {
				label: t(`userProfile:actionMenu.deleteFollow`),
				icon: "eye-slash",
				svgIconComponent: SolidEyeSlash,
				action: () => {
					dispatch(unfollowUser({ userId }))
						.unwrap()
						.then((data) => {
							if (data) {
								toast.success(
									t(
										"userProfile:successMessages.removeFollow"
									)
								);
								dispatch(
									setUserFollow({
										isFollowed: false,
										userId,
									})
								);
							}
						})
						.catch((error) => {
							toast.error(error.data?.error);
						});
				},
				disabled: isMe || isBan,
				tooltip: isBan ? t("suspensions:bannedAccount") : "",
				inDropdown: false,
			},
		!userStatus.blockByMe && {
			label: t(`userProfile:actionMenu.block`),
			icon: "ban",
			svgIconComponent: SolidBan,
			action: () => {
				if (!isAuth) {
					dispatch(openAuthModal());
				} else {
					dispatch(blockUser({ userId }))
						.unwrap()
						.then(() => {
							dispatch(
								getUserProfileMain({ login: userLogin, isAuth })
							);
							toast.success(
								t("userProfile:successMessages.block")
							);
						})
						.catch((error) => {
							toast.error(error.data?.error);
						});
				}
			},
			disabled: isMe,
			tooltip: isMe ? t("userProfile:tooltip.cantBlock") : null,
			inDropdown: true,
		},
		userStatus.blockByMe && {
			label: t(`userProfile:actionMenu.unblock`),
			icon: "ban",
			svgIconComponent: SolidBan,
			action: () => {
				dispatch(unblockUser({ userId }))
					.unwrap()
					.then(() => {
						dispatch(
							getUserProfileMain({ login: userLogin, isAuth })
						);
					})
					.catch((error) => {
						toast.error(error.data?.error);
					});
			},
			inDropdown: true,
		},
		{
			label: t(`userProfile:actionMenu.report`),
			icon: "exclamation-circle",
			svgIconComponent: SolidExclamationCircle,
			action: () => {
				if (!isAuth) {
					dispatch(openAuthModal());
				} else {
					const to = generatePath("/report/:idUser", {
						idUser: userId,
					});
					history.push(to);
				}
			},
			disabled: isMe || userStatus.isReport,
			tooltip: userStatus.isReport
				? t("userProfile:tooltip.cantReport")
				: isMe
				? t("userProfile:tooltip.cantReportYourself")
				: null,
			inDropdown: true,
		},
	].filter(Boolean);
};

// #####################################################

export default useActionItems;

// #####################################################
